/**
 * The Add Collector wizard's chrome — everything on the page that is the same
 * whether the step has loaded, is loading, or blew up.
 *
 * **Eager**, and shared with the skeleton for the reason `log-source-table-frame`
 * and `panel-frame` are: the step behind it is `lazy()`, so a skeleton that drew
 * its own copy of this header would be a second place for the wizard's title,
 * its rail and the card's explanation to drift.
 */
import { SectionDescription, SectionTitle } from '@/features/app-shell/section'
import { cn } from '@/utils/ui/styles'

import { CopyButton } from '@/components/buttons/copy-button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

import {
	COLLECTOR_HOST_REQUIREMENTS,
	describeTokenExpired,
	describeTokenValidity,
	INSTALL_COMMAND_SHELL,
} from '../../install-command'
import { WizardStepRail } from '../wizard-step-rail'

import type { ReactNode } from 'react'

/**
 * What the flow costs, in full, before it starts.
 *
 * Both steps are named even though only the first is built: the rail
 * is the promise that installing the agent is not the end of the job — a
 * Collector with no Log Sources on it receives nothing.
 */
const WIZARD_STEPS = ['Set up collector', 'Add log sources'] as const

/** Which step the rail marks as current. One-based, as the rail numbers it. */
const ACTIVE_STEP = 1

type AddCollectorFrameProps = {
	/** The step's body, inside the card. */
	children: ReactNode
	/**
	 * The step's actions, **below** the card rather than in a `CardFooter` — they
	 * move the wizard, they do not act on the card's contents, and the design
	 * frame puts them outside it for that reason.
	 */
	footer: ReactNode
}

export const AddCollectorFrame = ({
	children,
	footer,
}: AddCollectorFrameProps) => {
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="flex min-w-0 flex-col gap-1">
					<SectionTitle>Add remote collector</SectionTitle>
					<SectionDescription>
						On-prem collector · receives on-prem syslog and ships to AIR® ·
						Linux native install
					</SectionDescription>
				</div>

				<WizardStepRail steps={WIZARD_STEPS} activeStep={ACTIVE_STEP} />
			</div>

			<div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
				<Card>
					<CardHeader>
						<CardTitle>Install the collector</CardTitle>
						<CardDescription>
							{/* Says where the failures will be *before* anyone goes looking
								for them here: the installer runs on the customer's host, so
								AIR® never sees the error that stopped it. */}
							Run the one-liner on the collector host. Outbound HTTPS only — no
							inbound ports to open. Install errors appear in your terminal, not
							in AIR®.
						</CardDescription>
					</CardHeader>

					<CardContent className="flex flex-col gap-5">{children}</CardContent>
				</Card>

				<div className="flex items-center justify-end gap-3">{footer}</div>
			</div>
		</div>
	)
}

/**
 * What the host must be, as one line.
 *
 * A sentence rather than a checklist, because none of the three is something the
 * operator ticks off here — they are facts about the machine they are about to
 * run the command on, and a list of empty checkboxes would invite someone to
 * tick one.
 */
export const HostRequirements = () => {
	return (
		<div className="flex flex-col gap-1.5">
			<p className="text-sm font-medium">Requirements</p>

			<p className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-sm">
				{COLLECTOR_HOST_REQUIREMENTS.map(({ label, critical }, index) => (
					<span key={label} className="flex items-center gap-2">
						{index > 0 ? <span aria-hidden>·</span> : null}
						<span className={critical ? 'text-destructive-text' : undefined}>
							{label}
						</span>
					</span>
				))}
			</p>
		</div>
	)
}

type InstallCommandFrameProps = {
	/** The command itself — a `pre` when it is known, line boxes when it is not. */
	children: ReactNode
	/** The copy control, which only the resolved step can wire to a real string. */
	action: ReactNode
}

/**
 * The block the install one-liner is presented in: its label, its border, and
 * the shell chip that says what the command is written for.
 *
 * Shared with the skeleton, like everything else here — the chrome and the body
 * load at different times, and the block's height is what the card below it
 * depends on.
 */
export const InstallCommandFrame = ({
	children,
	action,
}: InstallCommandFrameProps) => {
	return (
		<div className="flex flex-col gap-1.5">
			<p className="text-sm font-medium">Run on the collector host</p>

			<div className="overflow-hidden rounded-lg border">
				<div className="bg-muted/40 flex items-center justify-between gap-2 border-b px-3 py-1.5">
					<span className="text-muted-foreground font-mono text-xs">
						{INSTALL_COMMAND_SHELL}
					</span>
					{action}
				</div>

				{children}
			</div>
		</div>
	)
}

type EnrollmentCommandProps = {
	/** Exactly as the API returned it — never trimmed, wrapped or rebuilt. */
	installCommand: string
	/** ISO 8601, from `enrollment_token_expires_at`. */
	expiresAt: string
	/** Past `expiresAt`: copy is disabled and the line says so. */
	isExpired?: boolean
	/** The explicit re-issue control shown once it has expired. */
	expiredAction?: ReactNode
}

/**
 * A minted install command: the block, its copy button, and how long it works.
 *
 * One line in a `pre` that scrolls sideways rather than wraps: the command is
 * a single line by design (a newline breaks the token header), and a soft
 * wrap invites a selection that picks up only half of it. The copy button
 * copies the string as it arrived, which is the path the screen expects
 * people to use.
 *
 * Shared by the wizard and the stand-alone "issue new token" action, so both
 * say the same thing about the same credential.
 */
export const EnrollmentCommand = ({
	installCommand,
	expiresAt,
	isExpired = false,
	expiredAction,
}: EnrollmentCommandProps) => {
	return (
		// `min-w-0`: the command is one long line, and inside a grid (the dialog)
		// an item's min-content width would stretch the dialog to fit it instead
		// of letting the `pre` scroll.
		<div className="flex min-w-0 flex-col gap-2">
			<InstallCommandFrame
				action={
					<CopyButton
						text={installCommand}
						size="icon-sm"
						disabled={isExpired}
						aria-label="Copy install command"
					/>
				}
			>
				<pre
					className={cn(
						'overflow-x-auto p-3 font-mono text-xs leading-relaxed whitespace-pre',
						isExpired && 'text-muted-foreground line-through',
					)}
				>
					{installCommand}
				</pre>
			</InstallCommandFrame>

			{isExpired ? (
				<div className="flex flex-wrap items-center justify-between gap-3">
					<p className="text-destructive-text text-sm">
						{describeTokenExpired(expiresAt)}
					</p>
					{expiredAction}
				</div>
			) : (
				<p className="text-muted-foreground text-sm">
					{describeTokenValidity(expiresAt)}
				</p>
			)}
		</div>
	)
}

type StaticIpAcknowledgementProps = {
	checked?: boolean
	onCheckedChange?: (checked: boolean) => void
	/** The skeleton's variant: the sentence is real, there is nothing to tick yet. */
	disabled?: boolean
}

/**
 * The one thing the operator has to agree to before leaving this screen.
 *
 * Owns its own label pairing rather than taking an id, so no caller can render
 * the sentence beside an unlabelled box — and the sentence itself lives here, in
 * the eager module, because the skeleton renders it for real (P-05 §1).
 *
 * The box is **nested inside** the label rather than paired with it by
 * `htmlFor`. That is a preference, not a workaround: both wire up. Base UI does
 * put a caller's `id` on the checkbox's hidden `<input>` (not on the `<span
 * role=checkbox>`, which keeps a generated one), and that input is a labelable
 * element, so an `htmlFor` pointing at it labels a control the browser can
 * activate. Measured in Chrome on both arrangements: one click on the sentence,
 * one `change` on the input, either way.
 */
export const StaticIpAcknowledgement = ({
	checked,
	onCheckedChange,
	disabled,
}: StaticIpAcknowledgementProps) => {
	return (
		<Label
			className={cn(
				'flex items-start gap-3 text-sm font-normal',
				disabled && 'text-muted-foreground',
			)}
		>
			<Checkbox
				checked={checked}
				onCheckedChange={(value) => onCheckedChange?.(value === true)}
				disabled={disabled}
			/>
			<span>
				I understand this host must keep a static IP — changing it will break
				ingestion.
			</span>
		</Label>
	)
}
